import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./App"; // Updated import path
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { initMsal, setStandaloneMode } from "./services/identity";
import { API_BASE_URL } from "../config";
import { logger } from "./utils/logger";
import "./styles/globals.css";

/* global document, Office, module, require, HTMLElement */

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const OFFICE_READY_TIMEOUT_MS = 2000;

/**
 * Detect whether we're hosted inside an Office app or running standalone in a browser
 * (the same taskpane.html opened directly at its URL). office.js is loaded either way;
 * outside a host, Office.onReady() resolves with no host (or never resolves) — so we race
 * it against a timeout and treat "no host" as standalone.
 */
async function isStandalone(): Promise<boolean> {
  try {
    if (typeof Office === "undefined" || !Office.onReady) {
      return true;
    }
    const readyInfo = await Promise.race([
      Office.onReady(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), OFFICE_READY_TIMEOUT_MS)),
    ]);
    // null => timed out; resolved object with falsy host => loaded outside a host.
    return !(readyInfo && (readyInfo as { host?: unknown }).host);
  } catch {
    return true;
  }
}

/* Boot: render once we know whether we're in an Office host or standalone */
(async () => {
  const version = process.env.BUILD_GIT_COMMIT
    ? `v${process.env.APP_VERSION} (${process.env.BUILD_GIT_COMMIT})`
    : `v${process.env.APP_VERSION}`;

  const standalone = await isStandalone();
  setStandaloneMode(standalone);
  logger.debug("[My AI Secretary]", version, "| API_BASE_URL:", API_BASE_URL, "| standalone:", standalone);

  // Initialize MSAL (NAA in-host, standard SPA standalone)
  try {
    await initMsal();
    logger.debug("MSAL initialized successfully");
  } catch (error) {
    logger.error("Failed to initialize MSAL:", error);
  }

  root?.render(
    <FluentProvider theme={webLightTheme}>
      <App standalone={standalone} />
    </FluentProvider>
  );
})();

if ((module as any).hot) {
  (module as any).hot.accept("./App", () => {  // Updated path
    const NextApp = require("./App").default;
    root?.render(
      <FluentProvider theme={webLightTheme}>
        <NextApp />
      </FluentProvider>
    );
  });
}